import { Axios } from "../../utils/axiosMiddleware";

/**
 * Account session for the Connect notification banner on the payout page.
 *
 * Returns `{clientSecret, accountId}`, or `{unavailable: true, reason}` where
 * reason is `noPayoutAccount` (a homeowner, or a gigster who never set up
 * payouts), `onboardingIncomplete` (Stripe renders no banner UI for an account
 * missing `details_submitted`) or `sessionRejected` (Stripe refused — most
 * likely our Connect Site links are not registered, without which a live-mode
 * Account Session cannot be created at all). All three mean "render nothing";
 * they stay distinct so the cause is legible rather than guessed at.
 *
 * The server reads `stripe_account_id` rather than provisioning one, so opening
 * the payout page never creates a Stripe account as a side effect.
 *
 * ⚠️ Deliberately does NOT call `SimpleCatchError`, unlike its siblings. That
 * helper ends in `message.error(...)`, so any non-401 failure would pop a red
 * toast showing the raw server message — or literally `undefined` when the body
 * has none. The banner is background furniture nobody asked for; a toast for it
 * is a visible failure on a page that is otherwise working fine. Returning
 * `undefined` silently lets the resolver render nothing, which is the whole
 * point. Same reasoning as `onboardingLink.js`, which bypasses the helper for a
 * state that is "a prompt … not a failure".
 *
 * No `token` argument either, unlike `taxSession.js`. That page is opened from
 * the app in the system browser with a `?token=` deep link; the banner mounts
 * only on the guarded `/stripe/onboarding` branch, where the request
 * interceptor always has a session. A token parameter here would be dead code
 * implying the banner works on the public return/refresh routes — it must not.
 */
export const createNotificationSession = async () => {
  try {
    const response = await Axios.post(
      "stripe/connect/notification-session",
      {}
    );
    return response?.data;
  } catch {
    return undefined;
  }
};
